import React, { useCallback, useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { ParticipantAttachedVideoDto, ParticipantCreateDto, ParticipantDto } from '../../../types/admin.types';
import {
    createParticipant,
    deleteParticipant,
    fetchParticipants,
    fetchParticipantVideos,
    updateParticipant
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import ParticipantFormFields, { normalizeParticipantCreatePayload } from './components/ParticipantFormFields';

const emptyForm: ParticipantCreateDto = {
    Name: '',
    Nickname: '',
    ProfileImageBase64: '',
    DateOfBirth: ''
};

const formatDate = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleDateString();
};

const formatPublishStatus = (status: number) => {
    if (status === 1) {
        return 'Published';
    }

    return 'Pending';
};

const AdminParticipants = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<ParticipantDto[]>([]);
    const [form, setForm] = useState<ParticipantCreateDto>(emptyForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAttachedVideos, setIsLoadingAttachedVideos] = useState(false);
    const [attachedVideos, setAttachedVideos] = useState<ParticipantAttachedVideoDto[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const token = getUserInfo()?.token;

    const loadItems = useCallback(async (query: string) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchParticipants(token, query);
            setItems(response);
        } catch (error) {
            setErrorMessage('Unable to load participants.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadItems(appliedSearch);
    }, [loadItems, appliedSearch]);

    const loadAttachedVideos = useCallback(async (participantId: string) => {
        setIsLoadingAttachedVideos(true);
        setErrorMessage(null);
        try {
            const response = await fetchParticipantVideos(participantId, token);
            setAttachedVideos(response);
        } catch (error) {
            setErrorMessage('Unable to load attached videos.');
            setAttachedVideos([]);
        } finally {
            setIsLoadingAttachedVideos(false);
        }
    }, [token]);

    useEffect(() => {
        if (!selectedId) {
            setAttachedVideos([]);
            setIsLoadingAttachedVideos(false);
            return;
        }

        loadAttachedVideos(selectedId);
    }, [selectedId, loadAttachedVideos]);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
        setAttachedVideos([]);
        setIsLoadingAttachedVideos(false);
    };

    const handleEdit = (item: ParticipantDto) => {
        setSelectedId(item.Id);
        setForm({
            Name: item.Name,
            Nickname: item.Nickname ?? '',
            ProfileImageBase64: item.ProfileImageBase64 ?? '',
            DateOfBirth: item.DateOfBirth ? item.DateOfBirth.split('T')[0] : ''
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);

        const payload: ParticipantCreateDto = normalizeParticipantCreatePayload(form);

        try {
            if (selectedId) {
                await updateParticipant({ ...payload, Id: selectedId }, token);
            } else {
                await createParticipant(payload, token);
            }
            await loadItems(appliedSearch);
            if (selectedId) {
                await loadAttachedVideos(selectedId);
            } else {
                resetForm();
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to save participant.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteParticipant(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems(appliedSearch);
        } catch (error) {
            setErrorMessage('Unable to delete participant.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Participants</h1>
                    <p>Manage fighters, interviewees, and other participants.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Current Participants</h2>
                    <div className={styles.actions}>
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search by name or nickname"
                            maxLength={200}
                        />
                        <button
                            type="button"
                            className={styles.secondary}
                            onClick={() => setAppliedSearch(searchInput)}
                        >
                            Search
                        </button>
                        <button
                            type="button"
                            className={styles.secondary}
                            onClick={() => {
                                setSearchInput('');
                                setAppliedSearch('');
                            }}
                        >
                            Clear
                        </button>
                    </div>
                    {isLoading ? (
                        <div className={styles.muted}>Loading participants...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>
                            {appliedSearch ? 'No participants match your search.' : 'No participants yet.'}
                        </div>
                    ) : (
                        items.map((item) => (
                            <div key={item.Id} className={styles.listItem}>
                                <div>
                                    <strong>{item.Name}</strong>
                                    <div className={styles.muted}>Attributes: {item.Attributes?.length ?? 0}</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.Id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Participant' : 'New Participant'}</h2>
                    <form onSubmit={handleSubmit}>
                        <ParticipantFormFields
                            form={form}
                            onChange={setForm}
                            fieldClassName={styles.field}
                            idPrefix="participant"
                        />
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Participant' : 'Create Participant'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                    {selectedId ? (
                        <div>
                            <h2>Attached Videos</h2>
                            {isLoadingAttachedVideos ? (
                                <div className={styles.muted}>Loading attached videos...</div>
                            ) : attachedVideos.length === 0 ? (
                                <div className={styles.empty}>No videos attached to this participant.</div>
                            ) : (
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Event</th>
                                            <th>Date</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attachedVideos.map((video) => (
                                            <tr key={video.id}>
                                                <td>{video.title}</td>
                                                <td>{video.eventName ?? '-'}</td>
                                                <td>{formatDate(video.effectiveEventDate ?? video.createdDate)}</td>
                                                <td>{formatPublishStatus(video.publishStatus)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}
                </section>
            </div>
        </div>
    );
};

export default AdminParticipants;
